import { useMemo, useState } from "react";
import { Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { GridValueGetterParams, GridRenderCellParams } from "@mui/x-data-grid";
import { lightGreen, red } from "@mui/material/colors";
import { Content } from "@/types";
import { getThumbnailImage } from "@/utils/media";
import { useAppSelector } from "@/hooks";
import { Copy01Icon, Delete02Icon, MoreVerticalIcon, ViewOffIcon, GiftIcon, PercentIcon, Money01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useDeleteContentMutation, useDuplicateContentMutation, useUpdateContentMutation } from "@/features/organizations/api/contentApi";
import pluralize from "pluralize";

interface UseContentColumnsProps {
    categories: any[];
}

export const useContentColumns = ({ categories }: UseContentColumnsProps): any[] => {
    const organizationId = useAppSelector((store) => store.ui.activeOrganization);
    const { palette } = useTheme();

    const columns: any[] = useMemo(
        () => [
            {
                flex: 1,
                field: "title",
                headerName: "ID",
                disableColumnMenu: true,
                renderCell: (params: GridValueGetterParams<Content>) => {
                    const image = getThumbnailImage({
                        file: params.row.images || [],
                        organizationId,
                    })

                    return (
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <img src={image} alt={params.row.title} width={50} height={50} style={{ borderRadius: "4px" }} />
                            <Typography variant="body1">{params.row.title}</Typography>
                        </Stack>
                    );
                },
            },
            {
                flex: 1,
                field: "type",
                headerName: "Tipo",
                disableColumnMenu: true,
                headerAlign: "center",
                align: "center",
                renderCell: (params: GridValueGetterParams<Content>) => {
                    const isOffer = params.row.type === "offer";

                    return (
                        <Chip
                            size="small"
                            label={isOffer ? "Oferta" : "Plato"}
                            sx={{
                                backgroundColor: isOffer ? red[100] : lightGreen[100],
                            }}
                        />
                    );
                },
            },
            {
                flex: 0.5,
                field: "category",
                headerName: "Categoría",
                valueGetter: (params: GridValueGetterParams<Content>) => {
                    if (params.row.type === "offer") return "Oferta";

                    if (!Array.isArray(params.row.category)) {
                        const category = categories.find((c) => c.id === params.row.category);
                        return category?.title;
                    }

                    return (params.row?.category ?? [])
                        .map((contentCategory) => {
                            const category = categories.find((c) => c.id === contentCategory);

                            if (category) {
                                return category.title;
                            }
                        })
                        .filter(Boolean)
                        .join(", ");
                },
            },
            {
                flex: 0.5,
                field: "loyalty",
                headerName: "Fidelización",
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<Content>) => {
                    const { isRedeemable, pointsCost, hideFromMenu } = params.row.loyalty || {};

                    if (!isRedeemable) return null;

                    return (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            {params.row?.loyalty?.rewardType === "item" ? (
                                <>
                                    <HugeiconsIcon icon={GiftIcon} color={palette.primary.main} />
                                    <Typography variant="body2">{pointsCost} {pluralize("pt", pointsCost)}</Typography>
                                </>
                            ) : null}

                            {params.row?.loyalty?.rewardType === "discount_pct" ? (
                                <>
                                    <HugeiconsIcon icon={PercentIcon} color={palette.primary.main} />
                                    <Typography variant="body2">
                                        {pointsCost} {pluralize("pt", pointsCost)} = {params.row?.loyalty?.rewardValue}%
                                    </Typography>
                                </>
                            ) : null}

                            {params.row?.loyalty?.rewardType === "discount_fixed" ? (
                                <>
                                    <HugeiconsIcon icon={Money01Icon} color={palette.primary.main} />
                                    <Typography variant="body2">
                                        {pointsCost} {pluralize("pt", pointsCost)} = C${params.row?.loyalty?.rewardValue}
                                    </Typography>
                                </>
                            ) : null}
                            {hideFromMenu && (
                                <Stack sx={{ color: "text.secondary" }} title="Oculto del menú público">
                                    <HugeiconsIcon icon={ViewOffIcon} size={16} />
                                </Stack>
                            )}
                        </Stack>
                    );
                },
            },
            {
                flex: 0.5,
                field: "actions",
                headerName: "Acciones",
                sortable: false,
                disableColumnMenu: true,
                align: "right",
                headerAlign: "right",
                renderCell: (params: GridRenderCellParams<Content>) => {
                    return <ActionMenu row={params.row} organizationId={organizationId} />;
                },
            },
        ],
        [categories, organizationId],
    );

    return columns;
};

const ActionMenu = ({ row, organizationId }: { row: Content; organizationId: string }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const [deleteContent] = useDeleteContentMutation();
    const [updateContent] = useUpdateContentMutation();
    const [duplicateContent] = useDuplicateContentMutation();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDelete = async () => {
        handleClose();
        const confirmDelete = window.confirm(`¿Estás seguro de que deseas eliminar "${row.title}"?`);
        if (confirmDelete) {
            await deleteContent({ organizationId, contentId: row.id });
        }
    };

    const handleDisable = async () => {
        handleClose();
        await updateContent({
            organizationId,
            contentId: row.id,
            content: { ...row, isDisabled: !row.isDisabled },
        });
    };

    const handleDuplicate = async () => {
        handleClose();
        await duplicateContent({ organizationId, contentId: row.id });
    };

    return (
        <>
            <IconButton onClick={handleClick}>
                <HugeiconsIcon icon={MoreVerticalIcon} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={handleDuplicate}>
                    <ListItemIcon>
                        <HugeiconsIcon icon={Copy01Icon} size={20} />
                    </ListItemIcon>
                    <ListItemText>Duplicar</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDisable}>
                    <ListItemIcon>
                        <HugeiconsIcon icon={ViewOffIcon} size={20} />
                    </ListItemIcon>
                    <ListItemText>{row.isDisabled ? "Habilitar" : "Deshabilitar"}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: red[500] }}>
                    <ListItemIcon sx={{ color: red[500] }}>
                        <HugeiconsIcon icon={Delete02Icon} size={20} />
                    </ListItemIcon>
                    <ListItemText>Eliminar</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};
